import {
  CertificatesContexts,
  getVesselCertificates,
  VRCertificateTypes,
  type VesselTypeCode,
  type VRCertificateType,
} from "@constants/certificates";
import { getVessels } from "@services/vesselsApi";
import { normalizeDate, today } from "@utils/date-time";
import { t, type TFunction } from "i18next";
import { z } from "zod";
import { OTHERS } from "../components/VesselOwnership/VesselOwnership";
import { CertificateFields, FORM_FIELDS } from "../utils";

export const VESSEL_REGISTRATION_REMARKS_MAX_CHARS = 1000;

const certificateFormSchema = z.object({
  [CertificateFields.URL]: z.string().optional(),
  [CertificateFields.DOCUMENT_NAME]: z.string().optional(),
  [CertificateFields.CLASS_NAME]: z.string().optional(),
  [CertificateFields.EXPIRY_DATE]: z.date().nullable().optional(),
  [CertificateFields.PERMANENT]: z.boolean().optional(),
});

const certificateIssuePath = (
  certType: VRCertificateType,
  field: keyof z.infer<typeof certificateFormSchema>,
) => [FORM_FIELDS.CERTIFICATES, certType, field];

type ImoExistsType = ((imo: string, currentVesselId?: string) => Promise<boolean>) & {
  cancel: () => void;
};

const makeImoExists = (): ImoExistsType => {
  let inFlight: AbortController | null = null;
  let callId = 0;
  let lastArg = "";
  let lastPromise: Promise<boolean> | null = null;

  const fn = ((normalizedImo: string, currentVesselId?: string) => {
    const cacheKey = `${normalizedImo}:${currentVesselId ?? ""}`;
    if (cacheKey === lastArg && lastPromise) return lastPromise;

    lastArg = cacheKey;
    callId += 1;
    const myId = callId;

    if (inFlight) inFlight.abort();
    const controller = new AbortController();
    inFlight = controller;

    const p = (async () => {
      try {
        const res = await getVessels(
          "bff-vessel",
          {
            imoNumber: normalizedImo,
            // two since one can be the current vessel being edited and we ignore that in the check,
            // so the other one would be the duplicate if it exists
            pageSize: 2,
            pageNumber: 1,
          },
          controller.signal,
        );
        const matchingVessels = Array.isArray(res?.vessels)
          ? res.vessels.filter((vessel) => vessel.id !== currentVesselId)
          : [];
        const exists = matchingVessels.length > 0;

        if (myId !== callId) return false;
        return exists;
      } catch {
        if (myId !== callId) return false;
        return false;
      } finally {
        if (myId === callId) inFlight = null;
      }
    })();

    lastPromise = p;
    return p;
  }) as ImoExistsType;

  fn.cancel = () => {
    if (inFlight) {
      inFlight.abort();
      inFlight = null;
    }
    lastPromise = null;
    lastArg = "";
  };

  return fn;
};

const checkImoExists = makeImoExists();

const normalizeImo = (val?: number): string => {
  const s = String(val ?? "").trim();
  return s;
};

const numberMax = (
  maxDigits: number,
  maxDecimals: number,
  label: string,
  maxValue?: number,
  required = false,
) =>
  z
    .string()
    .optional()
    .superRefine((val, ctx) => {
      if (!val) {
        if (!required) return;
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("form-validation.mandatory", { label }),
        });
        return;
      }
      if (!Number(val)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t(required ? "form-validation.mandatory" : "form-validation.invalid-number", {
            label,
          }),
        });
        return;
      }

      const [intPart, decPart] = val.split(".");

      if (intPart.length > maxDigits) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("form-validation.max-length-digits", { label, maxDigits }),
        });
      }
      if (decPart && decPart.length > maxDecimals) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("form-validation.max-length-decimals", { label, maxDecimals }),
        });
      }
      if (maxValue !== undefined && Number(val) > maxValue) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("form-validation.max-value", { label, maxValue }),
        });
      }
    })
    .pipe(z.coerce.number().optional());

const requiredStringMax = (max: number, label: string) =>
  z
    .string()
    .min(1, t("form-validation.mandatory", { label }))
    .max(max, t("form-validation.max-length", { label, max }));

const optionalStringMax = (max: number, label: string) =>
  z.string().max(max, t("form-validation.max-length", { label, max }));

const documentSchema = z.object({
  name: z.string(),
  url: z.string(),
  type: z.string().optional(),
});

const additionalDocumentMetadataSchema = {
  [FORM_FIELDS.DOCUMENT_TYPE]: z.string().optional(),
  [FORM_FIELDS.OTHER_SPECIFY_TYPE]: z.string().optional(),
  [FORM_FIELDS.DOCUMENT_NAME]: z.string().max(50).optional(),
  [FORM_FIELDS.DOCUMENT_SUMMARY]: z.string().max(300).optional(),
  [FORM_FIELDS.ISSUE_DATE]: z.union([z.date(), z.string(), z.null()]).optional(),
  [FORM_FIELDS.EXPIRY_DATE]: z.union([z.date(), z.string(), z.null()]).optional(),
};

const additionalDocumentFormMetadataSchema = {
  [FORM_FIELDS.DOCUMENT_TYPE]: z.string().optional(),
  [FORM_FIELDS.OTHER_SPECIFY_TYPE]: z.string().max(30).optional(),
  [FORM_FIELDS.DOCUMENT_NAME]: z.string().max(50).optional(),
  [FORM_FIELDS.DOCUMENT_SUMMARY]: z.string().max(300).optional(),
  [FORM_FIELDS.ISSUE_DATE]: z.union([z.date(), z.string(), z.null()]).optional(),
  [FORM_FIELDS.EXPIRY_DATE]: z.union([z.date(), z.string(), z.null()]).optional(),
};

const bunkerBargeAdditionalDocumentSchema = documentSchema.extend(additionalDocumentMetadataSchema);

// keep schema keys in sync with FORM_FIELDS if any changes are made
export const getVesselRegisterFormSchema = (
  t: TFunction,
  currentVesselId?: string,
  optionalFileds?: boolean,
  isDraft = false,
  isRestrictedToCertificatesUpdate = false,
  disableEffectiveDates = false,
) => {
  const requiredString = (max: number, label: string) =>
    isDraft ? optionalStringMax(max, label) : requiredStringMax(max, label);
  const requiredNumber = (
    maxDigits: number,
    maxDecimals: number,
    label: string,
    maxValue?: number,
  ) =>
    isDraft
      ? numberMax(maxDigits, maxDecimals, label, maxValue)
      : numberMax(maxDigits, maxDecimals, label, maxValue, true);

  const callSignSchema = optionalFileds
    ? optionalStringMax(15, t("vessel-management.call-sign")).regex(
        /^[A-Z0-9]*$/,
        t("form-validation.capital-letters-and-digits-only", {
          label: t("vessel-management.call-sign"),
        }),
      )
    : requiredString(15, t("vessel-management.call-sign")).regex(
        /^[A-Z0-9]*$/,
        t("form-validation.capital-letters-and-digits-only", {
          label: t("vessel-management.call-sign"),
        }),
      );
  const portOfRegistrationSchema = optionalFileds
    ? z.string().optional()
    : isDraft
      ? z.string().optional()
      : z.string().min(1, {
          message: t("form-validation.mandatory", {
            label: t("vessel-management.port-of-registration"),
          }),
        });
  const yearBuiltSchema = optionalFileds
    ? z.coerce.number().optional()
    : isDraft
      ? z.coerce.number().optional()
      : z.coerce
          .number()
          .optional()
          .superRefine((val, ctx) => {
            if (!val) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: t("form-validation.mandatory", {
                  label: t("vessel-management.ship-build-year"),
                }),
              });
            }
          });
  const flagSchema = optionalFileds
    ? z.string().optional()
    : isDraft
      ? z.string().optional()
      : z.string().min(1, {
          message: t("form-validation.mandatory", { label: t("vessel-management.flag") }),
        });

  // Vessel Type
  const vesselTypeCodeSchema = optionalFileds
    ? z.string().optional()
    : isDraft
      ? z.string().optional()
      : z.string().min(1, {
          message: t("form-validation.mandatory", { label: t("vessel-management.vessel-type") }),
        });
  const grossTonageSchema = optionalFileds
    ? numberMax(15, 3, t("vessel-management.gross-tonnage"))
    : requiredNumber(15, 3, t("vessel-management.gross-tonnage"));
  const netTonageSchema = optionalFileds
    ? numberMax(15, 3, t("vessel-management.net-tonnage"))
    : requiredNumber(15, 3, t("vessel-management.net-tonnage"));
  const deadweightTonageSchema = optionalFileds
    ? numberMax(11, 3, t("vessel-management.deadweight-tonnage"), 99999999999)
    : requiredNumber(11, 3, t("vessel-management.deadweight-tonnage"), 99999999999);

  // Vessel Dimensions
  const lengthOverallSchema = optionalFileds
    ? numberMax(15, 3, t("vessel-management.length-overall"))
    : requiredNumber(15, 3, t("vessel-management.length-overall"));
  const draftMaxSchema = optionalFileds
    ? numberMax(15, 3, t("vessel-management.maxDraft"))
    : requiredNumber(15, 3, t("vessel-management.maxDraft"));
  const beamSchema = optionalFileds
    ? numberMax(15, 3, t("vessel-management.beam"))
    : requiredNumber(15, 3, t("vessel-management.beam"));

  // Vessel Ownership
  const shipOwnerSchema = optionalFileds
    ? optionalStringMax(50, t("vessel-management.vessel-status-page.ownership.ship-owner"))
    : requiredString(50, t("vessel-management.vessel-status-page.ownership.ship-owner"));
  const shipsRegisteredOwnerSchema = optionalFileds
    ? optionalStringMax(
        50,
        t("vessel-management.vessel-status-page.ownership.ships-registered-owner"),
      )
    : requiredString(
        50,
        t("vessel-management.vessel-status-page.ownership.ships-registered-owner"),
      );
  const piClubSchema = optionalFileds
    ? z.string().optional()
    : isDraft
      ? z.string().optional()
      : z.string().min(1, {
          message: t("form-validation.mandatory", {
            label: t("vessel-management.name-ofp&i-club"),
          }),
        });
  const mlcClubSchema = optionalFileds
    ? z.string().optional()
    : isDraft
      ? z.string().optional()
      : z.string().min(1, {
          message: t("form-validation.mandatory", {
            label: t("vessel-management.name-mlc-of-p&i-club"),
          }),
        });

  return z
    .object({
      [FORM_FIELDS.VESSEL_NAME]: requiredString(50, t("vessel-management.vessel-name")),
      [FORM_FIELDS.NO_IMO_NUMBER]: z.boolean(),
      [FORM_FIELDS.OFFICIAL_NUMBER]: optionalStringMax(15, t("vessel-management.official-number")),
      [FORM_FIELDS.IMO]: z.coerce.number().optional(),
      [FORM_FIELDS.CALL_SIGN]: callSignSchema,
      [FORM_FIELDS.PORT_OF_REGISTRATION]: portOfRegistrationSchema,
      [FORM_FIELDS.FLAG]: flagSchema,
      [FORM_FIELDS.YEAR_BUILT]: yearBuiltSchema,
      [FORM_FIELDS.DATE_OF_DELIVERY]: z.union([z.date(), z.string(), z.null()]).optional(),

      // Vessel Type
      [FORM_FIELDS.VESSEL_TYPE_CODE]: vesselTypeCodeSchema,
      [FORM_FIELDS.BUNKER_REGISTERED]: z.boolean(),
      [FORM_FIELDS.DIPLOMATIC_VESSEL]: z.boolean(),
      [FORM_FIELDS.GROSS_TONNAGE]: grossTonageSchema,
      [FORM_FIELDS.NET_TONNAGE]: netTonageSchema,
      [FORM_FIELDS.DEADWEIGHT_TONNAGE]: deadweightTonageSchema,

      // Vessel Dimensions
      [FORM_FIELDS.LENGTH_OVERALL]: lengthOverallSchema,
      [FORM_FIELDS.DRAFT_MAX]: draftMaxSchema,

      [FORM_FIELDS.VESSEL_CLASSIFICATION_SOCIETY]: z.string().optional(),
      [FORM_FIELDS.OTHER_VESSEL_CLASSIFICATION_SOCIETY]: optionalStringMax(
        30,
        t("vessel-management.other-vessel-classification-society"),
      ).regex(/^[A-Za-z0-9\s]*$/, t("form-validation.no-special-characters-allowed")),
      [FORM_FIELDS.BEAM]: beamSchema,
      // Vessel Ownership
      [FORM_FIELDS.SHIP_OWNER]: shipOwnerSchema,
      [FORM_FIELDS.SHIP_CHARTERER]: optionalStringMax(
        150,
        t("vessel-management.vessel-status-page.ownership.ship-charterer"),
      ),
      [FORM_FIELDS.SHIPS_MANAGEMENT]: optionalStringMax(
        150,
        t("vessel-management.vessel-status-page.ownership.ships-management"),
      ),
      [FORM_FIELDS.SHIPS_REGISTERED_OWNER]: shipsRegisteredOwnerSchema,
      [FORM_FIELDS.PI_CLUB]: piClubSchema,
      [FORM_FIELDS.MLC_CLUB]: mlcClubSchema,

      [FORM_FIELDS.PI_CLUB_OTHER]: optionalStringMax(
        150,
        t("vessel-management.vessel-status-page.ownership.p&i-club-name"),
      ),
      [FORM_FIELDS.MLC_CLUB_OTHER]: optionalStringMax(
        150,
        t("vessel-management.vessel-status-page.ownership.mlc-p&i-club-name"),
      ),
      [FORM_FIELDS.REGISTRATION_REMARKS]: optionalStringMax(
        VESSEL_REGISTRATION_REMARKS_MAX_CHARS,
        t("vessel-management.remarks-instructions"),
      ).optional(),
      [FORM_FIELDS.ADDITIONAL_DOCUMENTS]: z
        .array(
          z
            .object({
              [FORM_FIELDS.FILE_NAME]: z.string(),
              [FORM_FIELDS.DOCUMENT_URL]: z.string(),
              ...additionalDocumentFormMetadataSchema,
            })
            .superRefine((document, ctx) => {
              const documentType = document[FORM_FIELDS.DOCUMENT_TYPE]?.trim();
              const documentName = document[FORM_FIELDS.DOCUMENT_NAME]?.trim();
              const otherSpecifyType = document[FORM_FIELDS.OTHER_SPECIFY_TYPE]?.trim();

              if (!isDraft && !documentType) {
                ctx.addIssue({
                  path: [FORM_FIELDS.DOCUMENT_TYPE],
                  code: z.ZodIssueCode.custom,
                  message: t("form-validation.mandatory", {
                    label: t("vessel-management.bunker-barge-page.document-type"),
                  }),
                });
              }
              if (!isDraft && !documentName) {
                ctx.addIssue({
                  path: [FORM_FIELDS.DOCUMENT_NAME],
                  code: z.ZodIssueCode.custom,
                  message: t("form-validation.mandatory", {
                    label: t("vessel-management.document-name"),
                  }),
                });
              }
              if (!isDraft && documentType === "OTHER_SPECIFY" && !otherSpecifyType) {
                ctx.addIssue({
                  path: [FORM_FIELDS.OTHER_SPECIFY_TYPE],
                  code: z.ZodIssueCode.custom,
                  message: t("form-validation.mandatory", {
                    label: t("vessel-management.bunker-barge-page.other-specify-type"),
                  }),
                });
              }

              if (
                !isDraft &&
                documentType === "CERTIFICATE" &&
                !document[FORM_FIELDS.EXPIRY_DATE]
              ) {
                ctx.addIssue({
                  path: [FORM_FIELDS.EXPIRY_DATE],
                  code: z.ZodIssueCode.custom,
                  message: t("form-validation.mandatory", {
                    label: t("vessel-management.bunker-barge-page.expiry-date"),
                  }),
                });
              }

              const issueDate = normalizeDate(document[FORM_FIELDS.ISSUE_DATE]);
              const expiryDate = normalizeDate(document[FORM_FIELDS.EXPIRY_DATE]);
              if (issueDate && expiryDate && expiryDate <= issueDate) {
                ctx.addIssue({
                  path: [FORM_FIELDS.EXPIRY_DATE],
                  code: z.ZodIssueCode.custom,
                  message: t("form-validation.must-be-after", {
                    subject: t("vessel-management.bunker-barge-page.expiry-date"),
                    object: t("vessel-management.bunker-barge-page.issue-date"),
                  }),
                });
              }
              if (expiryDate && expiryDate < today()) {
                ctx.addIssue({
                  path: [FORM_FIELDS.EXPIRY_DATE],
                  code: z.ZodIssueCode.custom,
                  message: t("form-validation.must-not-be-in-past", {
                    label: t("vessel-management.bunker-barge-page.expiry-date"),
                  }),
                });
              }
            }),
        )
        .optional(),
      [FORM_FIELDS.CERTIFICATES]: z.record(certificateFormSchema).optional(),
      // Bunker Barge Details, optional here, validated in superRefine since it depends on BUNKER_REGISTERED
      [FORM_FIELDS.NATURE_OF_SERVICE]: z.string().optional(),
      [FORM_FIELDS.BUNKERING_COMPANY]: z.string().optional(),
      [FORM_FIELDS.NAVIGATION_LICENSE_FROM_DATE]: z
        .union([z.date(), z.string(), z.null()])
        .optional(),
      [FORM_FIELDS.NAVIGATION_LICENSE_TO_DATE]: z
        .union([z.date(), z.string(), z.null()])
        .optional(),
      [FORM_FIELDS.NAVIGATION_LICENSE]: z.array(documentSchema).optional(),
      [FORM_FIELDS.CHARTER_PARTY_AGREEMENT]: z.array(documentSchema).optional(),
      [FORM_FIELDS.REGISTERED_OWNER]: z.string().optional(),
      [FORM_FIELDS.CHARTER_START_DATE]: z.union([z.date(), z.string(), z.null()]).optional(),
      [FORM_FIELDS.CHARTER_END_DATE]: z.union([z.date(), z.string(), z.null()]).optional(),
      [FORM_FIELDS.OIL_SPILL_COMPANY]: z.array(documentSchema).optional(),
      [FORM_FIELDS.OIL_COMPANY]: z.string().optional(),
      [FORM_FIELDS.OIL_SPILL_COMPANY_FROM_DATE]: z
        .union([z.date(), z.string(), z.null()])
        .optional(),
      [FORM_FIELDS.OIL_SPILL_COMPANY_TO_DATE]: z.union([z.date(), z.string(), z.null()]).optional(),
      [FORM_FIELDS.SIRE_REPORT]: z.array(documentSchema).optional(),
      [FORM_FIELDS.SIRE_REPORT_FROM_DATE]: z.union([z.date(), z.string(), z.null()]).optional(),
      [FORM_FIELDS.SIRE_REPORT_TO_DATE]: z.union([z.date(), z.string(), z.null()]).optional(),
      [FORM_FIELDS.HOSE_TEST_CERTIFICATE]: z.array(documentSchema).optional(),
      [FORM_FIELDS.COMPANY_NAME_HOSE_TEST_CERTIFICATE]: z
        .string()
        .max(
          50,
          t("form-validation.max-length", {
            label: t("vessel-management.bunker-barge-page.company-name"),
            max: 50,
          }),
        )
        .optional(),
      [FORM_FIELDS.HOSE_TEST_CERTIFICATE_EXPIRY_DATE]: z
        .union([z.date(), z.string(), z.null()])
        .optional(),
      [FORM_FIELDS.SHIP_TO_SHIP_PLAN]: z.array(documentSchema).optional(),
      [FORM_FIELDS.SHIP_TO_SHIP_PLAN_FROM_DATE]: z
        .union([z.date(), z.string(), z.null()])
        .optional(),
      [FORM_FIELDS.SHIP_TO_SHIP_PLAN_TO_DATE]: z.union([z.date(), z.string(), z.null()]).optional(),
      [FORM_FIELDS.EFFECTIVE_START_DATE]: z.union([z.date(), z.string(), z.null()]).optional(),
      [FORM_FIELDS.EFFECTIVE_END_DATE]: z.union([z.date(), z.string(), z.null()]).optional(),
      [FORM_FIELDS.REMARKS]: z.string().optional(),
      [FORM_FIELDS.BUNKER_BARGE_ADDITIONAL_DOCUMENTS]: z
        .array(bunkerBargeAdditionalDocumentSchema)
        .max(
          25,
          t("form-validation.max-length", {
            label: t("vessel-management.bunker-barge-page.form-sections.additional-documents"),
            max: 25,
          }),
        )
        .optional(),
    })
    .superRefine(async (schemaValues, ctx) => {
      for (const cert of Object.values(VRCertificateTypes) as VRCertificateType[]) {
        const certificate = schemaValues[FORM_FIELDS.CERTIFICATES]?.[cert];
        const url = certificate?.[CertificateFields.URL];
        const expiry = certificate?.[CertificateFields.EXPIRY_DATE];
        const permanent = certificate?.[CertificateFields.PERMANENT];
        const className = certificate?.[CertificateFields.CLASS_NAME];
        const isDiplomaticVessel = schemaValues[FORM_FIELDS.DIPLOMATIC_VESSEL];

        if (url && expiry && !permanent) {
          const expiryDate = normalizeDate(new Date(expiry));

          if (expiryDate && expiryDate < today() && !isDiplomaticVessel) {
            ctx.addIssue({
              path: certificateIssuePath(cert, CertificateFields.EXPIRY_DATE),
              code: z.ZodIssueCode.custom,
              message: t("form-validation.document-expired", {
                label: t("vessel-management.expiration-date"),
              }),
            });
          }
        }

        if (url && !expiry && !permanent && !isDiplomaticVessel && !isDraft) {
          ctx.addIssue({
            path: certificateIssuePath(cert, CertificateFields.EXPIRY_DATE),
            code: z.ZodIssueCode.custom,
            message: t("form-validation.mandatory", {
              label: t("vessel-management.expiration-date"),
            }),
          });
        }

        if (url && className && className.length > 50) {
          ctx.addIssue({
            path: certificateIssuePath(cert, CertificateFields.CLASS_NAME),
            code: z.ZodIssueCode.custom,
            message: t("form-validation.max-length", {
              label: t("vessel-management.registry-class-name"),
              max: 50,
            }),
          });
        }
      }
      if (
        !isDraft &&
        schemaValues[FORM_FIELDS.DIPLOMATIC_VESSEL] &&
        (!schemaValues[FORM_FIELDS.ADDITIONAL_DOCUMENTS] ||
          (schemaValues[FORM_FIELDS.ADDITIONAL_DOCUMENTS] ?? []).length === 0)
      ) {
        ctx.addIssue({
          path: [FORM_FIELDS.ADDITIONAL_DOCUMENTS],
          code: z.ZodIssueCode.custom,
          message: t("form-validation.mandatory", {
            label: t("vessel-management.diplomatic-clearance"),
          }),
        });
      }

      if (!isDraft && !schemaValues[FORM_FIELDS.NO_IMO_NUMBER] && !schemaValues[FORM_FIELDS.IMO]) {
        ctx.addIssue({
          path: [FORM_FIELDS.IMO],
          code: z.ZodIssueCode.custom,
          message: t("form-validation.mandatory", { label: t("vessel-management.imo-number") }),
        });
      }

      if (
        schemaValues[FORM_FIELDS.NO_IMO_NUMBER] &&
        !schemaValues[FORM_FIELDS.OFFICIAL_NUMBER]?.trim()
      ) {
        ctx.addIssue({
          path: [FORM_FIELDS.OFFICIAL_NUMBER],
          code: z.ZodIssueCode.custom,
          message: t("form-validation.mandatory", {
            label: t("vessel-management.official-number"),
          }),
        });
      }

      const dateOfDelivery = normalizeDate(schemaValues[FORM_FIELDS.DATE_OF_DELIVERY]);
      if (!isDraft && !optionalFileds && !dateOfDelivery) {
        ctx.addIssue({
          path: [FORM_FIELDS.DATE_OF_DELIVERY],
          code: z.ZodIssueCode.custom,
          message: t("form-validation.mandatory", {
            label: t("vessel-management.date-of-delivery"),
          }),
        });
      }
      if (dateOfDelivery) {
        const yearBuilt = schemaValues[FORM_FIELDS.YEAR_BUILT];
        if (yearBuilt && dateOfDelivery.getFullYear() < yearBuilt) {
          ctx.addIssue({
            path: [FORM_FIELDS.DATE_OF_DELIVERY],
            code: z.ZodIssueCode.custom,
            message: t("form-validation.must-be-after-value", {
              label: t("vessel-management.date-of-delivery"),
              value: t("vessel-management.ship-build-year"),
            }),
          });
        }

        if (dateOfDelivery > new Date()) {
          ctx.addIssue({
            path: [FORM_FIELDS.DATE_OF_DELIVERY],
            code: z.ZodIssueCode.custom,
            message: t("form-validation.must-not-be-in-future", {
              label: t("vessel-management.date-of-delivery"),
            }),
          });
        }
      }

      if (
        schemaValues[FORM_FIELDS.VESSEL_CLASSIFICATION_SOCIETY] === "OT" &&
        !schemaValues[FORM_FIELDS.OTHER_VESSEL_CLASSIFICATION_SOCIETY] &&
        !isDraft
      ) {
        ctx.addIssue({
          path: [FORM_FIELDS.OTHER_VESSEL_CLASSIFICATION_SOCIETY],
          code: z.ZodIssueCode.custom,
          message: t("form-validation.mandatory", {
            label: t("vessel-management.other-vessel-classification-society"),
          }),
        });
      }

      if (
        schemaValues[FORM_FIELDS.MLC_CLUB] === OTHERS &&
        !schemaValues[FORM_FIELDS.MLC_CLUB_OTHER] &&
        !isDraft
      ) {
        ctx.addIssue({
          path: [FORM_FIELDS.MLC_CLUB_OTHER],
          code: z.ZodIssueCode.custom,
          message: t("form-validation.mandatory", {
            label: t("vessel-management.other-mlc-p&i-club-name"),
          }),
        });
      }

      if (
        schemaValues[FORM_FIELDS.PI_CLUB] === OTHERS &&
        !schemaValues[FORM_FIELDS.PI_CLUB_OTHER] &&
        !isDraft
      ) {
        ctx.addIssue({
          path: [FORM_FIELDS.PI_CLUB_OTHER],
          code: z.ZodIssueCode.custom,
          message: t("form-validation.mandatory", {
            label: t("vessel-management.other-p&i-club"),
          }),
        });
      }
      if (
        !isDraft &&
        !optionalFileds &&
        schemaValues[FORM_FIELDS.VESSEL_TYPE_CODE] &&
        !schemaValues[FORM_FIELDS.DIPLOMATIC_VESSEL]
      ) {
        const requiredCerts = getVesselCertificates(
          CertificatesContexts.BASE,
          schemaValues[FORM_FIELDS.VESSEL_TYPE_CODE] as VesselTypeCode,
          schemaValues[FORM_FIELDS.GROSS_TONNAGE],
        )
          .filter((c) => c.required)
          .map((c) => c.type);

        for (const cert of requiredCerts) {
          const url = schemaValues[FORM_FIELDS.CERTIFICATES]?.[cert]?.[CertificateFields.URL];

          if (!url) {
            ctx.addIssue({
              path: certificateIssuePath(cert, CertificateFields.URL),
              code: z.ZodIssueCode.custom,
              message: t("form-validation.mandatory", {
                label: t(`vessel-management.certificates.${cert}`),
              }),
            });
          }
        }
      }

      if (!schemaValues[FORM_FIELDS.NO_IMO_NUMBER]) {
        const normalized = normalizeImo(schemaValues[FORM_FIELDS.IMO]);
        if (!normalized) {
          checkImoExists.cancel();
        } else if (normalized.length > 7) {
          checkImoExists.cancel();
          ctx.addIssue({
            path: [FORM_FIELDS.IMO],
            code: z.ZodIssueCode.custom,
            message: t("form-validation.max-length-digits", {
              label: t("vessel-management.imo-number"),
              maxDigits: 7,
            }),
          });
        } else {
          const exists = await checkImoExists(normalized, currentVesselId);
          if (exists) {
            ctx.addIssue({
              path: [FORM_FIELDS.IMO],
              code: z.ZodIssueCode.custom,
              message: t("form-validation.already-registered"),
            });
          }
        }
      }

      if (schemaValues[FORM_FIELDS.BUNKER_REGISTERED]) {
        const natureOfService = schemaValues[FORM_FIELDS.NATURE_OF_SERVICE];
        if (!natureOfService || !natureOfService.trim()) {
          if (!isDraft) {
            ctx.addIssue({
              path: [FORM_FIELDS.NATURE_OF_SERVICE],
              code: z.ZodIssueCode.custom,
              message: t("form-validation.mandatory", {
                label: t("vessel-management.bunker-barge-page.nature-of-service"),
              }),
            });
          }
        }

        const bunkeringCompany = schemaValues[FORM_FIELDS.BUNKERING_COMPANY];
        if (!bunkeringCompany || !bunkeringCompany.trim()) {
          if (!isDraft) {
            ctx.addIssue({
              path: [FORM_FIELDS.BUNKERING_COMPANY],
              code: z.ZodIssueCode.custom,
              message: t("form-validation.mandatory", {
                label: t("vessel-management.bunker-barge-page.company-name"),
              }),
            });
          }
        }

        const registeredOwner = schemaValues[FORM_FIELDS.REGISTERED_OWNER];
        if (!registeredOwner || !registeredOwner.trim()) {
          if (!isDraft) {
            ctx.addIssue({
              path: [FORM_FIELDS.REGISTERED_OWNER],
              code: z.ZodIssueCode.custom,
              message: t("form-validation.mandatory", {
                label: t("vessel-management.bunker-barge-page.register-owner"),
              }),
            });
          }
        } else if (registeredOwner.length > 150) {
          ctx.addIssue({
            path: [FORM_FIELDS.REGISTERED_OWNER],
            code: z.ZodIssueCode.custom,
            message: t("form-validation.max-length", {
              label: t("vessel-management.bunker-barge-page.register-owner"),
              max: 150,
            }),
          });
        }

        const companyName = schemaValues[FORM_FIELDS.OIL_COMPANY];
        if (!companyName || !companyName.trim()) {
          if (!isDraft) {
            ctx.addIssue({
              path: [FORM_FIELDS.OIL_COMPANY],
              code: z.ZodIssueCode.custom,
              message: t("form-validation.mandatory", {
                label: t("vessel-management.bunker-barge-page.company-name"),
              }),
            });
          }
        }

        const remarks = schemaValues[FORM_FIELDS.REMARKS];
        if (remarks && remarks.length > 1500) {
          ctx.addIssue({
            path: [FORM_FIELDS.REMARKS],
            code: z.ZodIssueCode.custom,
            message: t("form-validation.max-length", {
              label: t("vessel-management.bunker-barge-page.remarks"),
              max: 1500,
            }),
          });
        }

        const hasUploadedBunkerBargeDocument = (document: unknown): document is { url: string } =>
          !!document &&
          typeof document === "object" &&
          "url" in document &&
          typeof document.url === "string" &&
          Boolean(document.url);

        const requireBunkerBargeDocument = (field: keyof typeof schemaValues, labelKey: string) => {
          const documents = schemaValues[field];
          if (!Array.isArray(documents) || !documents.some(hasUploadedBunkerBargeDocument)) {
            if (!isDraft) {
              ctx.addIssue({
                path: [field],
                code: z.ZodIssueCode.custom,
                message: t("form-validation.mandatory", {
                  label: t(labelKey),
                }),
              });
            }
          }
        };

        const requireBunkerBargeDate = (field: keyof typeof schemaValues, labelKey: string) => {
          const date = normalizeDate(schemaValues[field]);
          if (!date && !isDraft) {
            ctx.addIssue({
              path: [field],
              code: z.ZodIssueCode.custom,
              message: t("form-validation.mandatory", {
                label: t(labelKey),
              }),
            });
          }
          return date;
        };

        const validateBunkerBargeDateRange = ({
          fromDate,
          toDate,
          toField,
          fromLabelKey,
          toLabelKey,
        }: {
          fromDate: Date | null;
          toDate: Date | null;
          toField: keyof typeof schemaValues;
          fromLabelKey: string;
          toLabelKey: string;
        }) => {
          if (fromDate && toDate && toDate <= fromDate) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: [toField],
              message: t("form-validation.must-be-after", {
                subject: t(toLabelKey),
                object: t(fromLabelKey),
              }),
            });
          }
          if (toDate && toDate < today()) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: [toField],
              message: t("form-validation.must-not-be-in-past", {
                label: t(toLabelKey),
              }),
            });
          }
        };

        requireBunkerBargeDocument(
          FORM_FIELDS.NAVIGATION_LICENSE,
          "vessel-management.bunker-barge-page.form-sections.navigation-license",
        );
        requireBunkerBargeDocument(
          FORM_FIELDS.CHARTER_PARTY_AGREEMENT,
          "vessel-management.bunker-barge-page.form-sections.charter-party-agreement",
        );
        requireBunkerBargeDocument(
          FORM_FIELDS.OIL_SPILL_COMPANY,
          "vessel-management.bunker-barge-page.form-sections.oil-spill-company-details",
        );
        requireBunkerBargeDocument(
          FORM_FIELDS.SIRE_REPORT,
          "vessel-management.bunker-barge-page.form-sections.sire-report",
        );
        requireBunkerBargeDocument(
          FORM_FIELDS.HOSE_TEST_CERTIFICATE,
          "vessel-management.bunker-barge-page.form-sections.hose-test-certificate",
        );
        requireBunkerBargeDocument(
          FORM_FIELDS.SHIP_TO_SHIP_PLAN,
          "vessel-management.bunker-barge-page.form-sections.ship-to-ship-plan",
        );

        const navigationFromDate = requireBunkerBargeDate(
          FORM_FIELDS.NAVIGATION_LICENSE_FROM_DATE,
          "vessel-management.bunker-barge-page.issue-date",
        );
        const navigationToDate = requireBunkerBargeDate(
          FORM_FIELDS.NAVIGATION_LICENSE_TO_DATE,
          "vessel-management.bunker-barge-page.expiry-date",
        );
        validateBunkerBargeDateRange({
          fromDate: navigationFromDate,
          toDate: navigationToDate,
          toField: FORM_FIELDS.NAVIGATION_LICENSE_TO_DATE,
          fromLabelKey: "vessel-management.bunker-barge-page.issue-date",
          toLabelKey: "vessel-management.bunker-barge-page.expiry-date",
        });

        const charterStartDate = requireBunkerBargeDate(
          FORM_FIELDS.CHARTER_START_DATE,
          "vessel-management.bunker-barge-page.from-date",
        );
        const charterEndDate = requireBunkerBargeDate(
          FORM_FIELDS.CHARTER_END_DATE,
          "vessel-management.bunker-barge-page.to-date",
        );
        validateBunkerBargeDateRange({
          fromDate: charterStartDate,
          toDate: charterEndDate,
          toField: FORM_FIELDS.CHARTER_END_DATE,
          fromLabelKey: "vessel-management.bunker-barge-page.from-date",
          toLabelKey: "vessel-management.bunker-barge-page.to-date",
        });

        const oilSpillFromDate = requireBunkerBargeDate(
          FORM_FIELDS.OIL_SPILL_COMPANY_FROM_DATE,
          "vessel-management.bunker-barge-page.from-date",
        );
        const oilSpillToDate = requireBunkerBargeDate(
          FORM_FIELDS.OIL_SPILL_COMPANY_TO_DATE,
          "vessel-management.bunker-barge-page.to-date",
        );
        validateBunkerBargeDateRange({
          fromDate: oilSpillFromDate,
          toDate: oilSpillToDate,
          toField: FORM_FIELDS.OIL_SPILL_COMPANY_TO_DATE,
          fromLabelKey: "vessel-management.bunker-barge-page.from-date",
          toLabelKey: "vessel-management.bunker-barge-page.to-date",
        });

        const sireReportFromDate = requireBunkerBargeDate(
          FORM_FIELDS.SIRE_REPORT_FROM_DATE,
          "vessel-management.bunker-barge-page.from-date",
        );
        const sireReportToDate = requireBunkerBargeDate(
          FORM_FIELDS.SIRE_REPORT_TO_DATE,
          "vessel-management.bunker-barge-page.to-date",
        );
        validateBunkerBargeDateRange({
          fromDate: sireReportFromDate,
          toDate: sireReportToDate,
          toField: FORM_FIELDS.SIRE_REPORT_TO_DATE,
          fromLabelKey: "vessel-management.bunker-barge-page.from-date",
          toLabelKey: "vessel-management.bunker-barge-page.to-date",
        });

        requireBunkerBargeDate(
          FORM_FIELDS.HOSE_TEST_CERTIFICATE_EXPIRY_DATE,
          "vessel-management.bunker-barge-page.expiry-date",
        );

        const shipToShipPlanFromDate = normalizeDate(
          schemaValues[FORM_FIELDS.SHIP_TO_SHIP_PLAN_FROM_DATE],
        );
        const shipToShipPlanToDate = normalizeDate(
          schemaValues[FORM_FIELDS.SHIP_TO_SHIP_PLAN_TO_DATE],
        );
        validateBunkerBargeDateRange({
          fromDate: shipToShipPlanFromDate,
          toDate: shipToShipPlanToDate,
          toField: FORM_FIELDS.SHIP_TO_SHIP_PLAN_TO_DATE,
          fromLabelKey: "vessel-management.bunker-barge-page.from-date",
          toLabelKey: "vessel-management.bunker-barge-page.to-date",
        });

        const hoseTestCompanyName = schemaValues[FORM_FIELDS.COMPANY_NAME_HOSE_TEST_CERTIFICATE];
        if (!hoseTestCompanyName?.trim() && !isDraft) {
          ctx.addIssue({
            path: [FORM_FIELDS.COMPANY_NAME_HOSE_TEST_CERTIFICATE],
            code: z.ZodIssueCode.custom,
            message: t("form-validation.mandatory", {
              label: t("vessel-management.bunker-barge-page.company-name"),
            }),
          });
        }

        const hoseTestExpiryDate = normalizeDate(
          schemaValues[FORM_FIELDS.HOSE_TEST_CERTIFICATE_EXPIRY_DATE],
        );
        if (hoseTestExpiryDate && hoseTestExpiryDate < today()) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [FORM_FIELDS.HOSE_TEST_CERTIFICATE_EXPIRY_DATE],
            message: t("form-validation.must-not-be-in-past", {
              label: t("vessel-management.bunker-barge-page.expiry-date"),
            }),
          });
        }

        const effectiveStartDate = normalizeDate(schemaValues[FORM_FIELDS.EFFECTIVE_START_DATE]);
        const effectiveEndDate = normalizeDate(schemaValues[FORM_FIELDS.EFFECTIVE_END_DATE]);

        if (
          !isRestrictedToCertificatesUpdate &&
          !disableEffectiveDates &&
          effectiveStartDate &&
          effectiveStartDate < today()
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [FORM_FIELDS.EFFECTIVE_START_DATE],
            message: t("form-validation.must-not-be-in-past", {
              label: t("vessel-management.bunker-barge-page.start-date"),
            }),
          });
        }
        if (effectiveEndDate && effectiveStartDate && effectiveEndDate <= effectiveStartDate) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [FORM_FIELDS.EFFECTIVE_END_DATE],
            message: t("form-validation.must-be-after", {
              subject: t("vessel-management.bunker-barge-page.end-date"),
              object: t("vessel-management.bunker-barge-page.start-date"),
            }),
          });
        }
        if (effectiveEndDate && effectiveEndDate < today()) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [FORM_FIELDS.EFFECTIVE_END_DATE],
            message: t("form-validation.must-not-be-in-past", {
              label: t("vessel-management.bunker-barge-page.end-date"),
            }),
          });
        }

        const bunkerBargeAdditionalDocuments =
          schemaValues[FORM_FIELDS.BUNKER_BARGE_ADDITIONAL_DOCUMENTS] ?? [];

        bunkerBargeAdditionalDocuments.forEach((document, idx) => {
          const documentType = document?.documentType?.trim();
          const documentName = document?.documentName?.trim();
          const otherSpecifyType = document?.otherSpecifyType?.trim();
          const issueDate = normalizeDate(document?.issueDate);
          const expiryDate = normalizeDate(document?.expiryDate);

          if (!isDraft && !documentType) {
            ctx.addIssue({
              path: [FORM_FIELDS.BUNKER_BARGE_ADDITIONAL_DOCUMENTS, idx, FORM_FIELDS.DOCUMENT_TYPE],
              code: z.ZodIssueCode.custom,
              message: t("form-validation.mandatory", {
                label: t("vessel-management.bunker-barge-page.document-type"),
              }),
            });
          }

          if (!isDraft && !documentName) {
            ctx.addIssue({
              path: [FORM_FIELDS.BUNKER_BARGE_ADDITIONAL_DOCUMENTS, idx, FORM_FIELDS.DOCUMENT_NAME],
              code: z.ZodIssueCode.custom,
              message: t("form-validation.mandatory", {
                label: t("vessel-management.document-name"),
              }),
            });
          }

          if (!isDraft && documentType === "OTHER_SPECIFY" && !otherSpecifyType) {
            ctx.addIssue({
              path: [
                FORM_FIELDS.BUNKER_BARGE_ADDITIONAL_DOCUMENTS,
                idx,
                FORM_FIELDS.OTHER_SPECIFY_TYPE,
              ],
              code: z.ZodIssueCode.custom,
              message: t("form-validation.mandatory", {
                label: t("vessel-management.bunker-barge-page.other-specify-type"),
              }),
            });
          }

          if (!isDraft && documentType === "CERTIFICATE" && !expiryDate) {
            ctx.addIssue({
              path: [FORM_FIELDS.BUNKER_BARGE_ADDITIONAL_DOCUMENTS, idx, FORM_FIELDS.EXPIRY_DATE],
              code: z.ZodIssueCode.custom,
              message: t("form-validation.mandatory", {
                label: t("vessel-management.bunker-barge-page.expiry-date"),
              }),
            });
          }

          if (issueDate && expiryDate && expiryDate <= issueDate) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: [FORM_FIELDS.BUNKER_BARGE_ADDITIONAL_DOCUMENTS, idx, FORM_FIELDS.EXPIRY_DATE],
              message: t("form-validation.must-be-after", {
                subject: t("vessel-management.bunker-barge-page.expiry-date"),
                object: t("vessel-management.bunker-barge-page.issue-date"),
              }),
            });
          }

          if (expiryDate && expiryDate < today()) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: [FORM_FIELDS.BUNKER_BARGE_ADDITIONAL_DOCUMENTS, idx, FORM_FIELDS.EXPIRY_DATE],
              message: t("form-validation.must-not-be-in-past", {
                label: t("vessel-management.bunker-barge-page.expiry-date"),
              }),
            });
          }
        });
      }
    });
};

export type VesselRegisterFormData = z.infer<ReturnType<typeof getVesselRegisterFormSchema>>;
